import { API, BACKEND_URL } from '@/config/api';
import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import AdminNavigation from '@/components/AdminNavigation';
import SEO from '@/components/SEO';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  BarChart, Bar, AreaChart, Area, ReferenceLine, ReferenceArea
} from 'recharts';
import { 
  Calendar, RefreshCw, Loader2, MessageSquare, Layers
} from 'lucide-react';
import OrganismSelector from '@/components/OrganismSelector';

// Import refactored components
import { areaCenters, locationCoordinates, riskConfig, CHART_COLORS as COLORS, getRiskLevel } from './AdminDashboard/constants';
import StatCards from './AdminDashboard/StatCards';
import ChatModal from './AdminDashboard/ChatModal';
import MapSection from './AdminDashboard/MapSection';
import OrganismOverviewTable from './AdminDashboard/OrganismOverviewTable';
import TrendAlerts from './AdminDashboard/TrendAlerts';

const NB_MONTHS = ['jan', 'feb', 'mar', 'apr', 'mai', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'des'];
const fmtDate = (s) => { if (!s) return ''; const d = new Date(s); return isNaN(d) ? s : `${d.getDate()}. ${NB_MONTHS[d.getMonth()]} ${d.getFullYear()}`; };
const getWeek = (s) => { const d = new Date(s); return Math.ceil((((d - new Date(d.getFullYear(),0,1)) / 86400000) + new Date(d.getFullYear(),0,1).getDay() + 1) / 7); };

// Custom dot component for the chart
const CustomDot = ({ cx, cy, payload, index, getColorForValue }) => {
  if (!payload) return null;
  const color = getColorForValue(payload.value);
  
  return (
    <g key={`dot-${index}`}>
      <circle
        cx={cx}
        cy={cy}
        r={7}
        fill={color}
        stroke="#fff"
        strokeWidth={2}
      />
    </g>
  );
};

// Custom active dot component for the chart
const CustomActiveDot = ({ cx, cy, payload, getColorForValue }) => {
  if (!payload) return null;
  const color = getColorForValue(payload.value);
  return (
    <circle
      cx={cx}
      cy={cy}
      r={9}
      fill={color}
      stroke="#fff"
      strokeWidth={3}
    />
  );
};

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [summary, setSummary] = useState(null);
  const [timeSeriesData, setTimeSeriesData] = useState([]);
  
  const [locationData, setLocationData] = useState([]);
  const [speciesData, setSpeciesData] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Algae trends state
  const [algaeTrendsData, setAlgaeTrendsData] = useState([]);
  const [availableOrganisms, setAvailableOrganisms] = useState([]);
  const [availableLocations, setAvailableLocations] = useState([]);
  const [availableSites, setAvailableSites] = useState([]);
  const [allSites, setAllSites] = useState([]); // All sites with area info for filtering
  const [selectedOrganism, setSelectedOrganism] = useState('all');
  const [selectedLocation, setSelectedLocation] = useState('all');
  const [selectedSite, setSelectedSite] = useState('all');
  const [loadingTrends, setLoadingTrends] = useState(false);
  const [thresholds, setThresholds] = useState(null);
  
  // Biomass trends state
  const [biomassTrendsData, setBiomassTrendsData] = useState([]);
  const [loadingBiomass, setLoadingBiomass] = useState(false);
  
  // Date range state for graph
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 90);  // Default to 90 days back
    return date.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => {
    return new Date().toISOString().split('T')[0];
  });
  
  // Map state
  const [mapLocations, setMapLocations] = useState([]);
  const [allMapLocations, setAllMapLocations] = useState([]); // Store all locations before filtering
  const [selectedMapMarker, setSelectedMapMarker] = useState(null);
  const [hoveredMapMarker, setHoveredMapMarker] = useState(null); // Track hovered marker
  const [selectedAreas, setSelectedAreas] = useState([]); // empty = all
  const [availableMapAreas, setAvailableMapAreas] = useState([]);
  const [allBulks, setAllBulks] = useState([]);
  const [selectedBulkId, setSelectedBulkId] = useState('latest');
  const [availableBulks, setAvailableBulks] = useState([]);
  
  // AI Chat state
  const [showChatModal, setShowChatModal] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [sessionId, setSessionId] = useState(`chat_${Date.now()}`);
  
  // Chart options
  const [useLogScale, setUseLogScale] = useState(false);
  const [selectedYear, setSelectedYear] = useState('');  // Empty = current period
  const [comparisonYears, setComparisonYears] = useState([]);  // Multiple years to compare
  const [comparisonData, setComparisonData] = useState({});  // Object keyed by year
  const [loadingComparison, setLoadingComparison] = useState(false);
  
  // Colors for comparison years
  const comparisonColors = {
    2024: '#a855f7',  // Purple
    2023: '#f97316',  // Orange  
    2022: '#06b6d4',  // Cyan
    2021: '#ec4899',  // Pink
    2020: '#84cc16',  // Lime
  };
  
  // Get available years for filtering
  const availableYears = [2025, 2024, 2023, 2022];
  const comparisonYearOptions = [2024, 2023, 2022, 2021, 2020];
  
  // Toggle comparison year
  const toggleComparisonYear = (year) => {
    setComparisonYears(prev => {
      if (prev.includes(year)) {
        return prev.filter(y => y !== year);
      } else {
        return [...prev, year].sort((a, b) => b - a);  // Sort descending
      }
    });
  };
  
  // Get color based on value and thresholds (interval-based)
  const getColorForValue = useCallback((value) => {
    if (!thresholds) return '#10b981'; // Default green
    
    const val = parseFloat(value);
    // Green: 0 to yellow threshold
    if (val < thresholds.yellow) return '#10b981'; // Green
    // Yellow: yellow to orange threshold (sitrongul)
    if (val < thresholds.orange) return '#eab308'; // Sitron yellow
    // Orange: orange to red threshold (lys orange)
    if (val < thresholds.red) return '#fb923c'; // Lys orange
    // Red: above red threshold
    return '#ef4444'; // Red
  }, [thresholds]);
  
  // Memoized dot components - depends on thresholds via getColorForValue
  const renderCustomDot = useCallback((props) => <CustomDot {...props} getColorForValue={getColorForValue} />, [getColorForValue]);
  const renderCustomActiveDot = useCallback((props) => <CustomActiveDot {...props} getColorForValue={getColorForValue} />, [getColorForValue]);
  
  useEffect(() => {
    fetchAllData();
  }, []);
  
  useEffect(() => {
    fetchAlgaeTrends();
  }, [startDate, endDate, selectedOrganism, selectedLocation, selectedSite]);
  
  // Filter map locations when areas change
  useEffect(() => {
    if (selectedAreas.length === 0) {
      setMapLocations(allMapLocations);
    } else {
      setMapLocations(allMapLocations.filter(loc => selectedAreas.includes(loc.area)));
    }
    setSelectedMapMarker(null);
  }, [selectedAreas, allMapLocations]);
  
  // Update available bulks when areas change
  useEffect(() => {
    if (!allBulks.length) return;
    const filtered = selectedAreas.length === 0
      ? allBulks 
      : allBulks.filter(b => selectedAreas.includes(b.area_name));
    setAvailableBulks(filtered);
    setSelectedBulkId('latest');
  }, [selectedAreas, allBulks]);
  
  // Rebuild map locations when selectedBulkId changes
  useEffect(() => {
    if (!allBulks.length) return;
    const bulksToProcess = selectedBulkId === 'latest' 
      ? allBulks 
      : allBulks.filter(b => b.id === selectedBulkId);
    if (bulksToProcess.length === 0) return;
    processMapLocations(bulksToProcess);
  }, [selectedBulkId]);
  
  const processMapLocations = (bulks, dynamicCoords = null, algaeTh = null, zooTh = null) => {
    const coordsLookup = dynamicCoords || dbLocationCoords;
    const algaeThMap = algaeTh || algaeThresholdMap;
    const zooThMap = zooTh || zooThresholdMap;
    const locationsMap = new Map();
    let locIndex = 0;
    const colorPri = { 'green': 0, 'yellow': 1, 'orange': 2, 'red': 3 };
    
    // Helper: resolve effective cell color
    // Prefer non-green override; else fallback to threshold-based computation from value.
    const resolveAlgaeColor = (orgKey, location, value, overrides) => {
      const ov = overrides[`${orgKey}_${location}`];
      if (ov && ov !== 'green') return ov;
      const parts = orgKey.split('_');
      const group = parts[0] || '';
      const species = parts.slice(1).join('_') || '';
      const isSum = species.toUpperCase().includes('SUM');
      const th = isSum ? algaeThMap[`__group_${group}`] : algaeThMap[orgKey];
      if (!th) return ov || 'green';
      const computed = computeThresholdColor(value, th);
      // Use the worse of override vs computed
      return colorPri[computed] >= colorPri[ov || 'green'] ? computed : (ov || 'green');
    };
    
    const resolveZooColor = (orgKey, location, value, overrides) => {
      const ov = overrides[`${orgKey}_${location}`];
      if (ov && ov !== 'green') return ov;
      const parts = orgKey.split('_');
      const species = parts.slice(1).join('_') || '';
      const th = zooThMap[species];
      if (!th) return ov || 'green';
      const computed = computeThresholdColor(value, th);
      return colorPri[computed] >= colorPri[ov || 'green'] ? computed : (ov || 'green');
    };
    
    bulks.forEach(bulk => {
      if (!bulk.algae_data) return;
      const colorOverrides = bulk.color_overrides || {};
      Object.entries(bulk.algae_data).forEach(([key, locationData]) => {
        Object.entries(locationData).forEach(([location, value]) => {
          if (!value || !value.trim()) return;
          const cleanName = location.includes(' - ') ? location.split(' - ')[1] : location;
          const cellColor = resolveAlgaeColor(key, location, value, colorOverrides);
          
          // Look up coordinates: 1) DB locations, 2) static constants, 3) fallback
          let coords = coordsLookup[cleanName] || locationCoordinates[cleanName];
          if (!coords) {
            const cn = cleanName.toLowerCase().replace(/[.]/g, '');
            for (const [ck, cv] of Object.entries({ ...coordsLookup, ...locationCoordinates })) {
              const kl = ck.toLowerCase().replace(/[.]/g, '');
              if (cn.includes(kl) || kl.includes(cn)) { coords = cv; break; }
            }
          }
          if (!coords) {
            const angle = (locIndex * 137.508) * (Math.PI / 180);
            const radius = 0.08 + (locIndex * 0.012);
            coords = { lat: 59.15 + radius * Math.cos(angle), lng: 5.85 + radius * Math.sin(angle) * 1.5 };
            locIndex++;
          }
          
          if (!locationsMap.has(cleanName)) {
            locationsMap.set(cleanName, {
              name: cleanName, fullName: location,
              letter: location.split(' - ')[0]?.trim() || cleanName[0],
              lat: coords.lat, lng: coords.lng,
              maxColor: 'green', zooColor: 'green', sampleCount: 0,
              area: bulk.area_name, date: bulk.date,
              temperature: bulk.temperature_data?.[location] || null,
              sampleDepth: bulk.sample_depth || null,
              colorHistory: [], organismData: {}
            });
          }
          const loc = locationsMap.get(cleanName);
          
          if (!loc.colorHistory.find(h => h.date === bulk.date)) {
            let wc = 'green';
            Object.entries(bulk.algae_data).forEach(([ok, od]) => {
              const c = resolveAlgaeColor(ok, location, od?.[location], colorOverrides);
              if (colorPri[c] > colorPri[wc]) wc = c;
            });
            loc.colorHistory.push({ date: bulk.date, color: wc });
          }
          
          if (bulk.date >= loc.date) {
            loc.sampleCount += 1; loc.date = bulk.date;
            if (bulk.temperature_data?.[location]) loc.temperature = bulk.temperature_data[location];
            if (bulk.sample_depth) loc.sampleDepth = bulk.sample_depth;
            if (Object.keys(loc.organismData).length === 0) {
              Object.entries(bulk.algae_data).forEach(([orgKey, orgLocData]) => {
                const val = orgLocData[location];
                const isSumRow = orgKey.toUpperCase().includes('SUM');
                // Include SUM rows even if empty (show as ~0), skip empty non-SUM
                if (val != null && (String(val).trim() !== '' || isSumRow)) {
                  const displayVal = (val === '' || val === null || val === undefined) ? '0' : val;
                  loc.organismData[orgKey] = {
                    value: displayVal,
                    color: resolveAlgaeColor(orgKey, location, val, colorOverrides)
                  };
                }
              });
            }
            if (bulk.zooplankton_data) {
              let wz = 'green';
              Object.entries(bulk.zooplankton_data).forEach(([ok, od]) => {
                const zc = resolveZooColor(ok, location, od?.[location], colorOverrides);
                if (colorPri[zc] > colorPri[wz]) wz = zc;
              });
              if (colorPri[wz] > colorPri[loc.zooColor]) loc.zooColor = wz;
            }
            if (colorPri[cellColor] > colorPri[loc.maxColor]) loc.maxColor = cellColor;
          }
        });
      });
    });
    
    const allLocs = Array.from(locationsMap.values());
    setAllMapLocations(allLocs);
    if (selectedAreas.length === 0) setMapLocations(allLocs);
    else setMapLocations(allLocs.filter(l => selectedAreas.includes(l.area)));
  };
  
  // Store DB location coordinates for dynamic lookup
  const [dbLocationCoords, setDbLocationCoords] = useState({});
  // Store threshold map for fallback color computation when overrides are missing/green
  const [algaeThresholdMap, setAlgaeThresholdMap] = useState({});
  const [zooThresholdMap, setZooThresholdMap] = useState({});
  
  /**
   * Compute threshold-based color for a value (mirrors backend _compute_threshold_color).
   * Returns 'green' if invalid/empty/below-yellow, or 'yellow'/'orange'/'red'.
   */
  const computeThresholdColor = (value, th) => {
    if (!th || value == null) return 'green';
    const s = String(value).trim();
    if (!s || s.startsWith('<') || s === '0' || s === '+') return 'green';
    const clean = s.replace(/[\s\u00A0]/g, '').replace(/,/g, '.');
    const n = parseFloat(clean);
    if (isNaN(n) || n === 0) return 'green';
    if (th.threshold_red == null) return 'green';
    if (n >= (th.threshold_red || 0)) return 'red';
    if (n >= (th.threshold_orange || 0)) return 'orange';
    if (n >= (th.threshold_yellow || 0)) return 'yellow';
    return 'green';
  };
  
  const fetchAllData = async () => {
    setLoading(true);
    
    try {
      const [statsRes, summaryRes, timeSeriesRes, locationRes, speciesRes, bulkRes, locsRes, algaeStructRes, zooStructRes] = await Promise.all([
        axios.get(`${API}/stats`),
        axios.get(`${API}/analytics/summary?days=30`),
        axios.get(`${API}/analytics/samples-over-time?days=30`),
        axios.get(`${API}/analytics/by-location?limit=10`),
        axios.get(`${API}/analytics/by-species?limit=6`),
        axios.get(`${API}/bulk-registrations`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/locations`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/organisms/algae-structure`, { withCredentials: true }).catch(() => ({ data: {} })),
        axios.get(`${API}/organisms/zooplankton-structure`, { withCredentials: true }).catch(() => ({ data: {} }))
      ]);
      
      setStats(statsRes.data);
      setSummary(summaryRes.data);
      setTimeSeriesData(timeSeriesRes.data);
      setLocationData(locationRes.data);
      setSpeciesData(speciesRes.data);
      
      // Build threshold maps (species + SUM group)
      const algaeTh = {};
      Object.entries(algaeStructRes.data || {}).forEach(([group, g]) => {
        const species = Array.isArray(g) ? g : (g?.species || []);
        species.forEach(sp => {
          algaeTh[`${group}_${sp.species}`] = {
            threshold_yellow: sp.threshold_yellow,
            threshold_orange: sp.threshold_orange,
            threshold_red: sp.threshold_red
          };
        });
        const gt = g?.group_thresholds;
        if (gt) {
          algaeTh[`__group_${group}`] = {
            threshold_yellow: gt.threshold_yellow,
            threshold_orange: gt.threshold_orange,
            threshold_red: gt.threshold_red
          };
        }
      });
      setAlgaeThresholdMap(algaeTh);
      
      const zooTh = {};
      Object.entries(zooStructRes.data || {}).forEach(([group, g]) => {
        const species = Array.isArray(g) ? g : (g?.species || []);
        species.forEach(sp => {
          zooTh[sp.species] = {
            threshold_yellow: sp.threshold_yellow,
            threshold_orange: sp.threshold_orange,
            threshold_red: sp.threshold_red
          };
        });
      });
      setZooThresholdMap(zooTh);
      
      // Build dynamic coordinate lookup from DB locations
      const dbCoords = {};
      if (Array.isArray(locsRes.data)) {
        locsRes.data.forEach(loc => {
          if (loc.name && loc.latitude && loc.longitude) {
            dbCoords[loc.name] = { lat: loc.latitude, lng: loc.longitude };
          }
        });
      }
      setDbLocationCoords(dbCoords);
      
      // Store bulks and process for map
      if (bulkRes.data && bulkRes.data.length > 0) {
        const sorted = [...bulkRes.data].sort((a, b) => new Date(b.date) - new Date(a.date));
        setAllBulks(sorted);
        
        // Extract unique areas
        const areas = [...new Set(sorted.map(b => b.area_name).filter(Boolean))];
        setAvailableMapAreas(areas);
        setAvailableBulks(sorted);
        
        // Process with all bulks (latest view) using DB coords + thresholds
        processMapLocations(sorted, dbCoords, algaeTh, zooTh);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Feil ved henting av data:', error);
      setLoading(false);
    }
  };
  
  const fetchAlgaeTrends = async () => {
    setLoadingTrends(true);
    // Clear existing data to prevent weird rendering artifacts
    setAlgaeTrendsData([]);
    
    try {
      const params = new URLSearchParams();
      
      // Use date range
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      
      if (selectedOrganism && selectedOrganism !== 'all') params.append('organism', selectedOrganism);
      if (selectedLocation && selectedLocation !== 'all') params.append('location', selectedLocation);
      if (selectedSite && selectedSite !== 'all') params.append('site', selectedSite);
      
      const res = await axios.get(`${API}/analytics/algae-trends?${params.toString()}`, {
        withCredentials: true
      });
      
      // Sort trends data by date and convert to timestamps for proper time scale
      // Also filter out any invalid data points
      const sortedTrends = (res.data.trends || [])
        .filter(trend => trend.date && trend.value !== undefined && trend.value !== null)
        .map(trend => ({
          ...trend,
          timestamp: new Date(trend.date).getTime(),
          value: parseFloat(trend.value) || 0
        }))
        .filter(trend => !isNaN(trend.timestamp) && isFinite(trend.timestamp))
        .sort((a, b) => a.timestamp - b.timestamp);
      
      setAlgaeTrendsData(sortedTrends);
      setThresholds(res.data.thresholds || null);
      
      if (res.data.organisms?.length > 0) {
        setAvailableOrganisms(res.data.organisms);
        // Auto-select Skeletonema as default (commonly has data), or keep 'all' if not found
        if (selectedOrganism === 'all' && res.data.organisms.length > 0) {
          // Try to find Kiselalger_Skeletonema as it usually has data
          const skeletonema = res.data.organisms.find(o => o.includes('Skeletonema'));
          if (skeletonema) {
            setSelectedOrganism(skeletonema);
          }
          // If not found, don't auto-select - let user choose
        }
      }
      if (res.data.locations?.length > 0) {
        setAvailableLocations(res.data.locations);
      }
      if (res.data.sites?.length > 0) {
        // Store all sites with area info for filtering
        setAllSites(res.data.sites);
        // Initially show all sites (just the names)
        setAvailableSites(res.data.sites.map(s => s.name || s));
      }
    } catch (error) {
      console.error('Feil ved henting av algetrend-data:', error);
    } finally {
      setLoadingTrends(false);
    }
  };
  
  // Fetch comparison data for multiple years
  const fetchComparisonData = async () => {
    if (comparisonYears.length === 0) {
      setComparisonData({});
      return;
    }
    
    setLoadingComparison(true);
    try {
      const currentYear = new Date(startDate).getFullYear();
      const startMonth = new Date(startDate).getMonth();
      const startDay = new Date(startDate).getDate();
      const endMonth = new Date(endDate).getMonth();
      const endDay = new Date(endDate).getDate();
      
      const allYearData = {};
      
      // Fetch data for each comparison year
      for (const year of comparisonYears) {
        const yearDiff = currentYear - year;
        
        // Calculate same period for this year
        const compStart = new Date(year, startMonth, startDay);
        const compEnd = new Date(year, endMonth, endDay);
        
        const params = new URLSearchParams();
        params.append('start_date', compStart.toISOString().split('T')[0]);
        params.append('end_date', compEnd.toISOString().split('T')[0]);
        
        if (selectedOrganism && selectedOrganism !== 'all') params.append('organism', selectedOrganism);
        if (selectedLocation && selectedLocation !== 'all') params.append('location', selectedLocation);
        if (selectedSite && selectedSite !== 'all') params.append('site', selectedSite);
        
        try {
          const res = await axios.get(`${API}/analytics/algae-trends?${params.toString()}`, {
            withCredentials: true
          });
          
          // Process data - shift timestamps to current year for overlay
          const compData = (res.data.trends || [])
            .filter(trend => trend.date && trend.value !== undefined)
            .map(trend => {
              const originalDate = new Date(trend.date);
              // Shift to current period for visual comparison
              const shiftedDate = new Date(originalDate);
              shiftedDate.setFullYear(shiftedDate.getFullYear() + yearDiff);
              return {
                ...trend,
                timestamp: shiftedDate.getTime(),
                originalDate: trend.date,
                value: parseFloat(trend.value) || 0,
                year: year
              };
            })
            .filter(trend => !isNaN(trend.timestamp))
            .sort((a, b) => a.timestamp - b.timestamp);
          
          if (compData.length > 0) {
            allYearData[year] = compData;
          }
        } catch (err) {
          console.error(`Feil ved henting av data for ${year}:`, err);
        }
      }
      
      setComparisonData(allYearData);
    } catch (error) {
      console.error('Feil ved henting av samanlikningsdata:', error);
      setComparisonData({});
    } finally {
      setLoadingComparison(false);
    }
  };
  
  // Fetch comparison when years change
  useEffect(() => {
    if (comparisonYears.length > 0 && startDate && endDate) {
      fetchComparisonData();
    } else {
      setComparisonData({});
    }
  }, [comparisonYears, startDate, endDate, selectedOrganism, selectedLocation, selectedSite]);
  
  // Helper to set year filter
  const setYearFilter = (year) => {
    if (year === 'last') {
      // Last year (same period)
      const now = new Date();
      const lastYearStart = new Date(now.getFullYear() - 1, 0, 1);
      const lastYearEnd = new Date(now.getFullYear() - 1, 11, 31);
      setStartDate(lastYearStart.toISOString().split('T')[0]);
      setEndDate(lastYearEnd.toISOString().split('T')[0]);
      setSelectedYear('last');
    } else if (year) {
      // Specific year
      setStartDate(`${year}-01-01`);
      setEndDate(`${year}-12-31`);
      setSelectedYear(year.toString());
    } else {
      // Reset to current 90 days
      const now = new Date();
      const start = new Date();
      start.setDate(start.getDate() - 90);
      setStartDate(start.toISOString().split('T')[0]);
      setEndDate(now.toISOString().split('T')[0]);
      setSelectedYear('');
    }
  };
  
  // Fetch biomass trends
  const fetchBiomassTrends = async () => {
    setLoadingBiomass(true);
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      
      const res = await axios.get(`${API}/analytics/biomass-trends?${params.toString()}`, {
        withCredentials: true
      });
      
      // Convert to chart format with timestamps
      const trends = (res.data.trends || [])
        .map(item => ({
          ...item,
          timestamp: new Date(item.date).getTime(),
          biomass: parseFloat(item.total_biomass) || 0
        }))
        .filter(item => !isNaN(item.timestamp))
        .sort((a, b) => a.timestamp - b.timestamp);
      
      setBiomassTrendsData(trends);
    } catch (error) {
      console.error('Feil ved henting av biomasse-data:', error);
    } finally {
      setLoadingBiomass(false);
    }
  };
  
  // Fetch biomass when dates change
  useEffect(() => {
    fetchBiomassTrends();
  }, [startDate, endDate]);
  
  // Filter sites when selected location changes
  useEffect(() => {
    if (selectedLocation === 'all') {
      // Show all sites with area name prefix
      setAvailableSites(allSites.map(s => ({
        name: s.name || s,
        displayName: s.area_name ? `${s.area_name} - ${s.name}` : s.name || s,
        area_name: s.area_name || ''
      })));
    } else {
      // Filter sites by selected area name - no need to show area prefix since area is already selected
      const filteredSites = allSites
        .filter(s => s.area_name === selectedLocation)
        .map(s => ({
          name: s.name || s,
          displayName: s.name || s,  // No area prefix when area is selected
          area_name: s.area_name || ''
        }));
      setAvailableSites(filteredSites);
      
      // Reset selected site if it's not in the filtered list
      if (selectedSite !== 'all' && !filteredSites.some(s => s.name === selectedSite)) {
        setSelectedSite('all');
      }
    }
  }, [selectedLocation, allSites]);
  
  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchAllData();
    await fetchAlgaeTrends();
    setRefreshing(false);
  };
  
  // AI Chat functions
  const handleSendMessage = async () => {
    if (!chatInput.trim() || chatLoading) return;
    
    const userMessage = chatInput.trim();
    setChatInput('');
    setChatLoading(true);
    
    // Add user message to chat
    setChatMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    
    try {
      const res = await axios.post(`${API}/chat-assistant`, {
        message: userMessage,
        session_id: sessionId
      }, {
        withCredentials: true
      });
      
      // Add AI response to chat
      setChatMessages(prev => [...prev, { role: 'assistant', content: res.data.response }]);
    } catch (error) {
      console.error('Chat error:', error);
      setChatMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Beklager, eg fekk ikkje til å svare akkurat no. Prøv igjen.' 
      }]);
    } finally {
      setChatLoading(false);
    }
  };
  
  if (loading) {
    return (
      <>
        <AdminNavigation />
        <div className="flex items-center justify-center min-h-screen bg-white">
          <div className="text-center">
            <div className="w-12 h-12 border-2 border-slate-300 border-t-slate-900 rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-slate-600">Laster...</p>
          </div>
        </div>
      </>
    );
  }
  
  return (
    <div className="flex min-h-screen bg-slate-50">
      <SEO 
        title="Admin Dashboard"
        description="Administrer algeovervåking, se prøveresultater og analyser data i Microsafe admin-portalen."
        url="/admin"
      />
      <AdminNavigation />
      <main className="flex-1 min-h-screen p-4 lg:p-6 overflow-x-hidden">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-2xl lg:text-3xl font-bold text-slate-900 mb-1 tracking-tight">Oversikt</h1>
            <p className="text-sm text-slate-600">Algeovervaking og prøveresultat</p>
          </div>
          
          
          {/* Trend alerts */}
          <TrendAlerts allBulks={allBulks} selectedAreas={selectedAreas} />
          
          {/* Map Section */}
          <MapSection
            mapLocations={mapLocations}
            selectedMapMarker={selectedMapMarker}
            setSelectedMapMarker={setSelectedMapMarker}
            hoveredMapMarker={hoveredMapMarker}
            setHoveredMapMarker={setHoveredMapMarker}
            selectedMapArea={selectedAreas.length === 0 ? 'all' : selectedAreas[0]}
            availableMapAreas={availableMapAreas}
            selectedAreas={selectedAreas}
            setSelectedAreas={setSelectedAreas}
            selectedBulkId={selectedBulkId}
            setSelectedBulkId={setSelectedBulkId}
            availableBulks={availableBulks}
            allBulks={allBulks}
            sampleDate={fmtDate(selectedBulkId !== 'latest' ? allBulks.find(b => b.id === selectedBulkId)?.date : mapLocations[0]?.date)}
          />
          
          {/* Organism Overview Table */}
          <OrganismOverviewTable mapLocations={mapLocations} allBulks={allBulks} />
        </div>
      </main>
      
      {/* Floating AI Chat Button */}
      {/* Floating AI Chat Button */}
      <button
        onClick={() => setShowChatModal(true)}
        className="fixed bottom-8 right-8 w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 text-white rounded-full shadow-2xl hover:shadow-3xl hover:scale-110 transition-all duration-300 flex items-center justify-center z-40"
        title="Chat med AI-assistent"
      >
        <MessageSquare className="w-7 h-7" />
      </button>
      
      {/* AI Chat Modal - Using refactored component */}
      <ChatModal
        isOpen={showChatModal}
        onClose={() => setShowChatModal(false)}
        chatMessages={chatMessages}
        setChatMessages={setChatMessages}
        chatInput={chatInput}
        setChatInput={setChatInput}
        chatLoading={chatLoading}
        setChatLoading={setChatLoading}
        sessionId={sessionId}
      />
    </div>
  );
}
